import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Cache de HTML por número de página.
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  // ---------------------------------------------------------------------------
  // Estado de paginação
  //
  // Os cursores substituem a leitura do DOM em #getPage(). O `this.refs` é
  // cacheado e invalidado de forma assíncrona, então logo após um append ele
  // ainda devolve os cards antigos — era daí que vinha o cálculo repetido da
  // mesma página (e o `page=2` travado na URL).
  // ---------------------------------------------------------------------------

  /** @type {number | null} Última página já renderizada para frente. */
  #nextCursor = null;

  /** @type {number | null} Primeira página já renderizada para trás. */
  #previousCursor = null;

  /** @type {Set<number>} Páginas já inseridas na grid. Rede de segurança final. */
  #renderedPages = new Set();

  /** @type {Map<number, Promise<void>>} Requisições em voo, por página. */
  #inFlight = new Map();

  /** @type {boolean} */
  #isLoadingNext = false;

  /** @type {boolean} */
  #isLoadingPrevious = false;

  /**
   * Incrementado a cada mudança de filtro. Renders que estavam aguardando um
   * fetch de um estado anterior descartam o resultado ao ver o token mudar.
   * @type {number}
   */
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#syncStateFromDom();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Lê o DOM UMA vez para descobrir quais páginas já estão renderizadas e
   * inicializar os cursores. Chamado no connect e após cada troca de filtro.
   */
  #syncStateFromDom() {
    this.#renderedPages.clear();
    this.#nextCursor = null;
    this.#previousCursor = null;

    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return;

    for (const card of cards) {
      const page = Number(card.dataset.page);
      if (Number.isFinite(page)) this.#renderedPages.add(page);
    }

    if (this.#renderedPages.size === 0) return;

    const rendered = [...this.#renderedPages];
    this.#previousCursor = Math.min(...rendered);
    this.#nextCursor = Math.max(...rendered);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const { viewMorePrevious, viewMoreNext } = this.refs;

            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === viewMoreNext) {
              this.#renderNextPage();
            }
          }
        },
        {
          // Mantido em 100px de propósito: o pré-carregamento antecipado é
          // desejável para UX. A race condition agora é tratada pelos guards,
          // não reduzindo a margem.
          rootMargin: '100px',
        }
      );
    }

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    // NaN passava por este guard na versão anterior (NaN < 1 e NaN > n são
    // ambos false), gerando requisições para `?page=NaN`.
    if (!Number.isFinite(pageInfo.page)) return false;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage);

    if (!Number.isFinite(lastPage) || pageInfo.page < 1 || pageInfo.page > lastPage) {
      return false;
    }

    return true;
  }

  /**
   * Pré-carrega a próxima/anterior página no cache, sem renderizar.
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) return;

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      // Pré-carregamento é best-effort: falha aqui não deve quebrar nada.
      console.warn('[paginated-list] prefetch failed', page.page, error);
    }
  }

  /**
   * Busca e cacheia o HTML de uma página.
   *
   * Requisições concorrentes para a mesma página compartilham a mesma promise,
   * o que elimina o fetch duplicado sem precisar do antigo par de resolvers
   * (que podia ser sobrescrito por um segundo render e deixar o primeiro
   * aguardando indefinidamente).
   *
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @returns {Promise<void>}
   */
  #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.pages.has(pageNumber)) return Promise.resolve();

    const existing = this.#inFlight.get(pageNumber);
    if (existing) return existing;

    let targetUrl = url;
    if (!targetUrl) {
      targetUrl = new URL(window.location.href);
      targetUrl.searchParams.set('page', pageNumber.toString());
      targetUrl.hash = '';
    }

    if (!this.#shouldUsePage({ page: pageNumber, url: targetUrl })) {
      return Promise.resolve();
    }

    const request = (async () => {
      try {
        // IMPORTANTE: `getSectionHTML` muta a URL recebida (adiciona
        // `section_id`). Clonamos para que o objeto usado no `history.pushState`
        // do render não seja contaminado — isso vazava `?section_id=...` para a
        // barra de endereço e quebrava a página no refresh.
        const requestUrl = new URL(targetUrl);
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, requestUrl);
        this.pages.set(pageNumber, pageContent);
      } finally {
        // Removido em sucesso e em erro, para permitir retry na próxima
        // tentativa de scroll.
        this.#inFlight.delete(pageNumber);
      }
    })();

    this.#inFlight.set(pageNumber, request);

    return request;
  }

  async #renderNextPage() {
    if (this.#isLoadingNext) return;

    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    if (this.#renderedPages.has(nextPage.page)) return;

    this.#isLoadingNext = true;
    const token = this.#generation;

    try {
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);

        // Filtro mudou ou componente saiu do DOM enquanto aguardávamos.
        if (token !== this.#generation || !this.isConnected) return;

        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      // Recheca após o await: outro render pode ter inserido esta página.
      if (this.#renderedPages.has(nextPage.page)) return;

      this.#renderedPages.add(nextPage.page);
      this.#nextCursor = nextPage.page;

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', this.#toPublicUrl(nextPage.url));

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } catch (error) {
      console.error('[paginated-list] failed to render next page', error);
    } finally {
      this.#isLoadingNext = false;
    }
  }

  async #renderPreviousPage() {
    if (this.#isLoadingPrevious) return;

    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    if (this.#renderedPages.has(previousPage.page)) return;

    this.#isLoadingPrevious = true;
    const token = this.#generation;

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);

      if (!previousPageItemElements) {
        await this.#fetchSpecificPage(previousPage.page, previousPage.url);

        if (token !== this.#generation || !this.isConnected) return;

        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      if (this.#renderedPages.has(previousPage.page)) return;

      this.#renderedPages.add(previousPage.page);
      this.#previousCursor = previousPage.page;

      // Guarda a posição de scroll e a altura do primeiro elemento
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', this.#toPublicUrl(previousPage.url));

      // Reajusta o scroll para manter a mesma vista
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } catch (error) {
      console.error('[paginated-list] failed to render previous page', error);
    } finally {
      this.#isLoadingPrevious = false;
    }
  }

  /**
   * Calcula a próxima página a partir do cursor interno, com fallback para o
   * DOM apenas quando o cursor ainda não foi inicializado.
   *
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';

    let base = isPrevious ? this.#previousCursor : this.#nextCursor;

    if (base == null) {
      const { cards } = this.refs;
      if (!Array.isArray(cards)) return;

      const targetCard = cards[isPrevious ? 0 : cards.length - 1];
      if (!targetCard) return;

      base = Number(targetCard.dataset.page);
      if (!Number.isFinite(base)) return;
    }

    const page = isPrevious ? base - 1 : base + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * Remove parâmetros internos que nunca devem aparecer na barra de endereço.
   * Segunda camada de proteção: se algum utilitário voltar a mutar a URL, o
   * usuário ainda recebe um link navegável e compartilhável.
   *
   * @param {URL} url
   * @returns {string}
   */
  #toPublicUrl(url) {
    const publicUrl = new URL(url);
    publicUrl.searchParams.delete('section_id');
    publicUrl.hash = '';
    return publicUrl.toString();
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages and paging state.
   */
  #handleFilterUpdate = () => {
    // Invalida renders que estejam aguardando um fetch do estado anterior.
    this.#generation++;

    this.pages.clear();
    this.#inFlight.clear();

    this.#renderedPages.clear();
    this.#nextCursor = null;
    this.#previousCursor = null;

    this.#isLoadingNext = false;
    this.#isLoadingPrevious = false;

    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // Espera o DOM ser atualizado com o conteúdo filtrado.
    const observer = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) return;

        // Reconstrói os cursores a partir do novo conteúdo antes de voltar a
        // observar — caso contrário o primeiro scroll recalcularia a página
        // com base no estado antigo.
        this.#syncStateFromDom();

        this.#observeViewMore();
        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        observer.disconnect();
      }, 3000);
    }
  };
}